import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import * as ExcelJS from 'exceljs';
import { useAuth } from '../../components/auth/AuthContext';
import { clientService } from '../../services/api/clientService';
import { bankService } from '../../services/api/bankService';
import AlertModal from '../../components/common/AlertModal';
import { getBankDisplayName } from '../../utils/bankUtils';
import { logger } from '../../utils/logger';
import ConfirmationComparisonFieldsSettings from '../../components/settings/ConfirmationComparisonFieldsSettings';
import WorkflowConfigPanel from '../../components/settings/WorkflowConfigPanel';
import FXGOSettings from '../../components/settings/FXGOSettings';
import type { FXGODefaultsSettings } from '../../components/settings/FXGOSettings';
import type { FXGOSettings as FXGOSettingsData } from '../../services/api/clientService';
import { ValuationPreferences, DEFAULT_VALUATION_PREFERENCES, ExpiryToleranceSettings, DEFAULT_EXPIRY_TOLERANCES, ExpiryEmailConfig } from '../../services/api/clientService';
import EmailConnectionSettings from '../../components/settings/EmailConnectionSettings';
import AccountingConfigPanel from '../../components/settings/AccountingConfigPanel';
import GroupingsConfigPanel from '../../components/settings/GroupingsConfigPanel';
import TradeImageCaptureConfigPanel from '../../components/settings/TradeImageCaptureConfigPanel';
import CustomDocumentsConfigPanel from '../../components/settings/CustomDocumentsConfigPanel';
import UsersPanel from './UsersPanel';
import ValuationPreferencesSettings from '../../components/settings/ValuationPreferencesSettings';
import ContractsAdminPanel from '../../components/settings/ContractsAdminPanel';
import SettlementInstructionsAdminPanel from '../../components/settings/SettlementInstructionsAdminPanel';
import EntityAliasesPanel from '../../components/settings/EntityAliasesPanel';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import './AdminDashboard.css';

interface AlertSettings {
  emailConfirmedTrades: {
    enabled: boolean;
    emails: string[];
  };
  emailDisputedTrades: {
    enabled: boolean;
    emails: string[];
  };
  emailAutoSettlement: {
    enabled: boolean;
    emails: string[];
  };
  smsConfirmedTrades: {
    enabled: boolean;
    phones: string[];
  };
  smsDisputedTrades: {
    enabled: boolean;
    phones: string[];
  };
  smsAutoSettlement: {
    enabled: boolean;
    phones: string[];
  };
}

interface AutomationSettings {
  dataSharing: boolean;
  autoConfirmMatched: {
    enabled: boolean;
    delayMinutes: number;
  };
  autoCartaInstruccion: boolean;
  autoConfirmDisputed: {
    enabled: boolean;
    delayMinutes: number;
  };
  autoConfirmOnMatch: boolean;
  autoExpiryResponse: boolean;
}

interface BankAccount {
  id: string;
  active: boolean;
  accountName: string;
  bankName: string;
  swiftCode: string;
  accountCurrency: string;
  accountNumber: string;
  isDefault: boolean;
  accountType?: 'nostro' | 'vostro';
  counterpartyId?: string;
  counterpartyName?: string;
}

interface ExpectedField {
  name: string;
  type: 'string' | 'number' | 'date' | 'enum';
  format?: string;
  required: boolean;
  enumValues?: string[];
  description: string;
}

interface FieldMapping {
  sourceField: string;
  targetField: string;
  transformation?: {
    type: 'direct' | 'format' | 'enum' | 'split' | 'combine';
    params?: any;
  };
}

interface DataMapping {
  id: string;
  name: string;
  description: string;
  fileType: 'csv' | 'excel' | 'json';
  createdDate: string;
  fieldMappings: FieldMapping[];
  sampleData?: any[];
}

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Expected fields for trade data mapping (based on ClientTradesGrid columns, excluding status and source)
  const EXPECTED_FIELDS: ExpectedField[] = [
    { name: 'tradeId', type: 'string', format: 'text', required: true, description: 'Unique identifier for the trade' },
    { name: 'counterparty', type: 'string', format: 'text', required: true, description: 'Bank or financial institution counterparty' },
    { name: 'productType', type: 'enum', format: 'text', required: true, enumValues: ['FX Spot', 'FX Forward'], description: 'Type of financial product' },
    { name: 'tradeDate', type: 'date', format: 'YYYY-MM-DD', required: true, description: 'Date when trade was executed' },
    { name: 'valueDate', type: 'date', format: 'YYYY-MM-DD', required: true, description: 'Settlement/value date for the trade' },
    { name: 'currency1', type: 'enum', format: 'ISO code', required: true, enumValues: ['USD', 'CLP', 'EUR', 'GBP', 'CLF'], description: 'Primary currency (3-letter ISO code)' },
    { name: 'currency2', type: 'enum', format: 'ISO code', required: true, enumValues: ['USD', 'CLP', 'EUR', 'GBP', 'CLF'], description: 'Secondary currency (3-letter ISO code)' },
    { name: 'amount', type: 'number', format: 'decimal', required: true, description: 'Trade amount/notional value' },
    { name: 'price', type: 'number', format: 'decimal(4)', required: true, description: 'Exchange rate or price (4 decimal places)' },
    { name: 'paymentDate', type: 'date', format: 'YYYY-MM-DD', required: true, description: 'Date when payment is due' }
  ];
  
  const [searchParams] = useSearchParams();
  const validTabs = ['confirmations', 'accounts', 'workflow', 'fxgo', 'email', 'accounting', 'groupings', 'imagecapture', 'customdocuments', 'contracts', 'settlementinstructions', 'forwardexpiries', 'users', 'valuation', 'entityaliases'] as const;
  type AdminTab = typeof validTabs[number];
  const visibleTabs = new Set<string>(
    user?.role === 'client_acc_user' ? ['accounting'] : validTabs
  );
  const defaultTab: AdminTab = user?.role === 'client_acc_user' ? 'accounting' : 'confirmations';
  const requestedTab = searchParams.get('tab') as AdminTab;
  const initialTab = (validTabs.includes(requestedTab) && visibleTabs.has(requestedTab)) ? requestedTab : defaultTab;
  const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);

  const [automationSettings, setAutomationSettings] = useState<AutomationSettings>({
    dataSharing: true,
    autoConfirmMatched: {
      enabled: true,
      delayMinutes: 30
    },
    autoCartaInstruccion: false,
    autoConfirmDisputed: {
      enabled: false,
      delayMinutes: 60
    },
    autoConfirmOnMatch: false,
    autoExpiryResponse: false,
  });

  // Banks loaded from database (used by ConfirmationComparisonFieldsSettings + settlement accounts)
  const [availableBanks, setAvailableBanks] = useState<Array<{id: string; name: string; swiftCode?: string; isCounterparty?: boolean}>>([]);

  const [alertSettings, setAlertSettings] = useState<AlertSettings>({
    emailConfirmedTrades: {
      enabled: true,
      emails: ['admin@palace.cl', 'trades@palace.cl']
    },
    emailDisputedTrades: {
      enabled: true,
      emails: ['admin@palace.cl', 'disputes@palace.cl']
    },
    emailAutoSettlement: {
      enabled: false,
      emails: []
    },
    smsConfirmedTrades: {
      enabled: false,
      phones: ['+56912345678']
    },
    smsDisputedTrades: {
      enabled: true,
      phones: ['+56912345678', '+56987654321']
    },
    smsAutoSettlement: {
      enabled: false,
      phones: []
    }
  });
  
  const [newEmailConfirmed, setNewEmailConfirmed] = useState('');
  const [newEmailDisputed, setNewEmailDisputed] = useState('');
  const [newEmailAutoSettlement, setNewEmailAutoSettlement] = useState('');
  const [newPhoneConfirmed, setNewPhoneConfirmed] = useState('');
  const [newPhoneDisputed, setNewPhoneDisputed] = useState('');
  const [newPhoneAutoSettlement, setNewPhoneAutoSettlement] = useState('');
  
  // Bank Accounts state
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [accountTypeFilter, setAccountTypeFilter] = useState<'nostro' | 'vostro'>('nostro');

  // Accounts state
  const [editingAccount, setEditingAccount] = useState<string | null>(null);
  const [accountForm, setAccountForm] = useState<Partial<BankAccount>>({});
  const [accountGrouping, setAccountGrouping] = useState<'none' | 'bank' | 'currency' | 'counterparty'>('none');
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  
  // Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'info' | 'warning' | 'error' | 'success';
    onConfirm?: () => void;
    confirmLabel?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Per-group lazy loading flags
  const [clientSettingsLoaded, setClientSettingsLoaded] = useState(false);
  const [clientSettingsLoading, setClientSettingsLoading] = useState(false);
  const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
  const [bankAccountsLoading, setBankAccountsLoading] = useState(false);
  const [fxgoSettingsLoaded, setFxgoSettingsLoaded] = useState(false);
  const [fxgoSettingsLoading, setFxgoSettingsLoading] = useState(false);

  // Data Mapping state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedData, setUploadedData] = useState<any[] | null>(null);
  const [sourceFields, setSourceFields] = useState<string[]>([]);
  const [currentMapping, setCurrentMapping] = useState<FieldMapping[]>([]);
  const [mappedData, setMappedData] = useState<any[] | null>(null);
  const [dataMappings, setDataMappings] = useState<DataMapping[]>([]);
  const [showMappingForm, setShowMappingForm] = useState(false);
  const [editingMapping, setEditingMapping] = useState<DataMapping | null>(null);
  const [mappingForm, setMappingForm] = useState<{name: string, description: string}>({name: '', description: ''});
  const [dragOverUpload, setDragOverUpload] = useState(false);

  // FXGO Settings state
  const [fxgoSettings, setFxgoSettings] = useState<FXGODefaultsSettings | null>(null);
  const [isSavingFxgo, setIsSavingFxgo] = useState(false);

  // Valuation Preferences state
  const [valuationPreferences, setValuationPreferences] = useState<ValuationPreferences>(DEFAULT_VALUATION_PREFERENCES);
  const [valuationPreferencesLoaded, setValuationPreferencesLoaded] = useState(false);
  const [valuationPreferencesLoading, setValuationPreferencesLoading] = useState(false);
  const [isSavingValuationPreferences, setIsSavingValuationPreferences] = useState(false);

  // Expiry Tolerance Settings state
  const [expiryTolerances, setExpiryTolerances] = useState<ExpiryToleranceSettings>(DEFAULT_EXPIRY_TOLERANCES);
  const [expiryTolerancesLoaded, setExpiryTolerancesLoaded] = useState(false);
  const [isSavingExpiryTolerances, setIsSavingExpiryTolerances] = useState(false);
  // Raw text for tolerance inputs — allows typing "5." or "0," without losing the trailing separator
  const [tolTexts, setTolTexts] = useState<Record<string, string>>({});

  // Expiry Email Config state
  const [expiryEmailRows, setExpiryEmailRows] = useState<{ counterpartyId: string; emails: string[] }[]>([]);
  const [expiryEmailCc, setExpiryEmailCc] = useState<string[]>([]);
  const [expiryEmailConfigLoaded, setExpiryEmailConfigLoaded] = useState(false);
  const [newExpiryEmailInputs, setNewExpiryEmailInputs] = useState<Record<number, string>>({});
  const [newCcEmailInput, setNewCcEmailInput] = useState('');

  // --- Lazy per-tab data loaders (idempotent — no-op if already loaded) ---

  const loadClientSettingsData = useCallback(async () => {
    if (clientSettingsLoaded || clientSettingsLoading) return;
    const orgId = user?.profile?.organization?.id;
    if (!orgId) {
      setLoadError('No organization ID found');
      return;
    }

    setClientSettingsLoading(true);
    try {
      const response = await clientService.getClientSettings(orgId);
      if (response.success && response.data) {
        setAutomationSettings(response.data.automation);

        const alertsWithDefaults = {
          ...response.data.alerts,
          emailAutoSettlement: response.data.alerts.emailAutoSettlement || { enabled: false, emails: [] },
          smsAutoSettlement: response.data.alerts.smsAutoSettlement || { enabled: false, phones: [] }
        };
        setAlertSettings(alertsWithDefaults);
      }

      // Also load banks for counterparty dropdown (used in automation + settlement)
      const banksResponse = await bankService.listBanks();
      if (banksResponse.success) {
        const banks = banksResponse.data
          .filter(bank => bank.id)
          .map(bank => ({ id: bank.id!, name: bank.name, swiftCode: bank.swiftCode, isCounterparty: bank.isCounterparty }));
        setAvailableBanks(banks);
      }

      setClientSettingsLoaded(true);
    } catch (error) {
      logger.error('Failed to load client settings:', error);
      setLoadError(error instanceof Error ? error.message : 'Failed to load settings');
    } finally {
      setClientSettingsLoading(false);
    }
  }, [clientSettingsLoaded, clientSettingsLoading, user?.profile?.organization?.id]);

  const loadBankAccountsData = useCallback(async () => {
    if (bankAccountsLoaded || bankAccountsLoading) return;
    const orgId = user?.profile?.organization?.id;
    if (!orgId) return;

    setBankAccountsLoading(true);
    try {
      const accountsResponse = await clientService.getBankAccounts(orgId);
      if (accountsResponse.success && accountsResponse.data) {
        const accounts = accountsResponse.data.map(account => ({
          id: account.id || '',
          active: account.active,
          accountName: account.accountName,
          bankName: account.bankName,
          swiftCode: account.swiftCode,
          accountCurrency: account.accountCurrency,
          accountNumber: account.accountNumber,
          isDefault: account.isDefault,
          accountType: account.accountType || 'nostro' as const,
          counterpartyId: account.counterpartyId,
          counterpartyName: account.counterpartyName,
        }));
        setBankAccounts(accounts);
      }
      setBankAccountsLoaded(true);
    } catch (error) {
      logger.error('Failed to load bank accounts:', error);
      setLoadError(error instanceof Error ? error.message : 'Failed to load bank accounts');
    } finally {
      setBankAccountsLoading(false);
    }
  }, [bankAccountsLoaded, bankAccountsLoading, user?.profile?.organization?.id]);

  const loadFxgoSettingsData = useCallback(async () => {
    if (fxgoSettingsLoaded || fxgoSettingsLoading) return;
    const orgId = user?.profile?.organization?.id;
    if (!orgId) return;

    setFxgoSettingsLoading(true);
    try {
      const fxgoResponse = await clientService.getFXGOSettings(orgId);
      if (fxgoResponse.success && fxgoResponse.data) {
        setFxgoSettings(fxgoResponse.data as unknown as FXGODefaultsSettings);
      }
      setFxgoSettingsLoaded(true);
    } catch (error) {
      logger.error('Failed to load FXGO settings:', error);
      // Non-blocking: FXGO settings will use defaults
      setFxgoSettingsLoaded(true);
    } finally {
      setFxgoSettingsLoading(false);
    }
  }, [fxgoSettingsLoaded, fxgoSettingsLoading, user?.profile?.organization?.id]);

  const loadValuationPreferencesData = useCallback(async () => {
    if (valuationPreferencesLoaded || valuationPreferencesLoading) return;
    const orgId = user?.profile?.organization?.id;
    if (!orgId) return;

    setValuationPreferencesLoading(true);
    try {
      const response = await clientService.getValuationPreferences(orgId);
      if (response.success && response.data) {
        setValuationPreferences(response.data);
      }
      setValuationPreferencesLoaded(true);
    } catch (error) {
      logger.error('Failed to load valuation preferences:', error);
      setValuationPreferencesLoaded(true);
    } finally {
      setValuationPreferencesLoading(false);
    }
  }, [valuationPreferencesLoaded, valuationPreferencesLoading, user?.profile?.organization?.id]);

  const loadDataForTab = useCallback(async (tab: string) => {
    switch (tab) {
      case 'confirmations':
        await loadClientSettingsData();
        break;
      case 'accounts':
        await loadClientSettingsData();
        await loadBankAccountsData();
        break;
      case 'fxgo':
        await loadFxgoSettingsData();
        break;
      case 'valuation':
        await loadValuationPreferencesData();
        break;
      case 'forwardexpiries':
        await loadClientSettingsData();
        if (!expiryTolerancesLoaded) {
          const orgId = user?.profile?.organization?.id;
          if (orgId) {
            try {
              const resp = await clientService.getExpiryTolerances(orgId);
              if (resp.success && resp.data) { setExpiryTolerances(resp.data); setTolTexts({}); }
            } catch (e) { /* defaults are fine */ }
            setExpiryTolerancesLoaded(true);
          }
        }
        if (!expiryEmailConfigLoaded) {
          const orgId = user?.profile?.organization?.id;
          if (orgId) {
            try {
              const resp = await clientService.getExpiryEmailConfig(orgId);
              if (resp.success && resp.data) {
                const entries = Object.entries(resp.data.counterpartyExpiryEmails || {});
                setExpiryEmailRows(entries.map(([counterpartyId, emails]) => ({
                  counterpartyId,
                  emails: emails ? emails.split(';').map((e: string) => e.trim()).filter(Boolean) : []
                })));
                const ccStr = resp.data.expiryEmailCc || '';
                setExpiryEmailCc(ccStr ? ccStr.split(';').map((e: string) => e.trim()).filter(Boolean) : []);
              }
            } catch (e) { /* defaults are fine */ }
            setExpiryEmailConfigLoaded(true);
          }
        }
        break;
      // email, workflow, mapping, contracts, settlementinstructions, groupings, imagecapture, customdocuments, users — self-loading child components
    }
  }, [loadClientSettingsData, loadBankAccountsData, loadFxgoSettingsData, loadValuationPreferencesData]);

  // Load only the initial tab's data on mount (respects ?tab= URL param)
  useEffect(() => {
    if (user?.profile?.organization?.id) {
      loadDataForTab(initialTab);
    }
  }, [user?.profile?.organization?.id, loadDataForTab]);
  
  const handleAutomationChange = (key: keyof AutomationSettings, value: any) => {
    setAutomationSettings(prev => {
      const newSettings = {
        ...prev,
        [key]: value
      };
      
      // If turning off autoConfirmMatched, also turn off autoCartaInstruccion
      if (key === 'autoConfirmMatched' && !value.enabled) {
        newSettings.autoCartaInstruccion = false;
      }
      
      // If turning on autoConfirmMatched and autoCartaInstruccion was previously on, keep it on
      // Otherwise, default behavior applies
      
      return newSettings;
    });

  };
  

  const addEmail = (alertType: 'emailConfirmedTrades' | 'emailDisputedTrades' | 'emailAutoSettlement') => {
    const email = alertType === 'emailConfirmedTrades' ? newEmailConfirmed :
                  alertType === 'emailDisputedTrades' ? newEmailDisputed : newEmailAutoSettlement;
    const setEmail = alertType === 'emailConfirmedTrades' ? setNewEmailConfirmed :
                     alertType === 'emailDisputedTrades' ? setNewEmailDisputed : setNewEmailAutoSettlement;
    
    if (email && email.includes('@')) {
      if (alertSettings[alertType].emails.includes(email)) {
        // Show duplicate alert modal
        setAlertModal({
          isOpen: true,
          title: t('admin.modal.duplicateEmail.title'),
          message: t('admin.modal.duplicateEmail.message'),
          type: 'warning'
        });
        return;
      }
      
      setAlertSettings(prev => ({
        ...prev,
        [alertType]: {
          ...prev[alertType],
          emails: [...prev[alertType].emails, email]
        }
      }));
      setEmail('');
  
    }
  };
  
  const removeEmail = (alertType: 'emailConfirmedTrades' | 'emailDisputedTrades' | 'emailAutoSettlement', email: string) => {
    setAlertSettings(prev => ({
      ...prev,
      [alertType]: {
        ...prev[alertType],
        emails: prev[alertType].emails.filter((e: string) => e !== email)
      }
    }));

  };
  
  const addPhone = (alertType: 'smsConfirmedTrades' | 'smsDisputedTrades' | 'smsAutoSettlement') => {
    const phone = alertType === 'smsConfirmedTrades' ? newPhoneConfirmed :
                  alertType === 'smsDisputedTrades' ? newPhoneDisputed : newPhoneAutoSettlement;
    const setPhone = alertType === 'smsConfirmedTrades' ? setNewPhoneConfirmed :
                     alertType === 'smsDisputedTrades' ? setNewPhoneDisputed : setNewPhoneAutoSettlement;
    
    if (phone && phone.startsWith('+')) {
      if (alertSettings[alertType].phones.includes(phone)) {
        // Show duplicate alert modal
        setAlertModal({
          isOpen: true,
          title: t('admin.modal.duplicatePhone.title'),
          message: t('admin.modal.duplicatePhone.message'),
          type: 'warning'
        });
        return;
      }
      
      setAlertSettings(prev => ({
        ...prev,
        [alertType]: {
          ...prev[alertType],
          phones: [...prev[alertType].phones, phone]
        }
      }));
      setPhone('');
  
    }
  };
  
  const removePhone = (alertType: 'smsConfirmedTrades' | 'smsDisputedTrades' | 'smsAutoSettlement', phone: string) => {
    setAlertSettings(prev => ({
      ...prev,
      [alertType]: {
        ...prev[alertType],
        phones: prev[alertType].phones.filter((p: string) => p !== phone)
      }
    }));

  };

  const closeModal = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false, onConfirm: undefined, confirmLabel: undefined }));
  };


  // Account management functions
  const handleAddAccount = () => {
    const newAccount: BankAccount = {
      id: 'NEW_ACCOUNT', // Special marker for new accounts
      active: true,
      accountName: '',
      bankName: '',
      swiftCode: '',
      accountCurrency: '',
      accountNumber: '',
      isDefault: false,
      accountType: accountTypeFilter,
    };
    setBankAccounts(prev => [...prev, newAccount]);
    setEditingAccount(newAccount.id);
    setAccountForm({ ...newAccount });

  };

  const handleEditAccount = (account: BankAccount) => {
    setEditingAccount(account.id);
    setAccountForm({ ...account });
  };

  const handleSaveAccount = async () => {
    // Prevent double-clicking
    if (isSavingAccount) return;
    
    setIsSavingAccount(true);
    
    try {
      if (!accountForm.accountName || !accountForm.bankName || !accountForm.swiftCode || 
          !accountForm.accountCurrency || !accountForm.accountNumber) {
        setAlertModal({
          isOpen: true,
          title: 'Validation Error',
          message: 'Please fill in all required fields.',
          type: 'warning'
        });
        return;
      }

      if (!user?.profile?.organization?.id) {
        setAlertModal({
          isOpen: true,
          title: 'Error',
          message: 'No organization ID found',
          type: 'error'
        });
        return;
      }

      if (editingAccount === 'NEW_ACCOUNT') {
        // Create new account
        const response = await clientService.createBankAccount(user.profile.organization.id, {
          accountName: accountForm.accountName!,
          bankName: accountForm.bankName!,
          swiftCode: accountForm.swiftCode!,
          accountCurrency: accountForm.accountCurrency!,
          accountNumber: accountForm.accountNumber!,
          isDefault: accountForm.isDefault ?? false,
          accountType: accountForm.accountType || 'nostro',
          counterpartyId: accountForm.counterpartyId,
          counterpartyName: accountForm.counterpartyName,
        });

        if (response.success && response.data) {
          // Replace the temporary account with the saved account (with server-generated id)
          setBankAccounts(prev => prev.map(account =>
            account.id === 'NEW_ACCOUNT' ? response.data as BankAccount : account
          ));
        }
      } else if (editingAccount) {
        // Update existing account
        const response = await clientService.updateBankAccount(user.profile.organization.id, editingAccount, {
          accountName: accountForm.accountName!,
          bankName: accountForm.bankName!,
          swiftCode: accountForm.swiftCode!,
          accountCurrency: accountForm.accountCurrency!,
          accountNumber: accountForm.accountNumber!,
          active: accountForm.active ?? true,
          isDefault: accountForm.isDefault ?? false,
          accountType: accountForm.accountType || 'nostro',
          counterpartyId: accountForm.counterpartyId,
          counterpartyName: accountForm.counterpartyName,
        });

        if (response.success) {
          setBankAccounts(prev => prev.map(account => 
            account.id === editingAccount ? { ...accountForm as BankAccount } : account
          ));
        }
      }

      setEditingAccount(null);
      setAccountForm({});
    } catch (error) {
      setAlertModal({
        isOpen: true,
        title: 'Save Error',
        message: error instanceof Error ? error.message : 'Failed to save bank account',
        type: 'error'
      });
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleCancelAccount = () => {
    if (editingAccount === 'NEW_ACCOUNT') {
      // Remove new empty account if cancelled
      setBankAccounts(prev => prev.filter(acc => acc.id !== 'NEW_ACCOUNT'));
    }
    setEditingAccount(null);
    setAccountForm({});
  };

  const handleDeleteAccount = (accountId: string) => {
    // If it's a new unsaved account, just remove from local state
    if (accountId === 'NEW_ACCOUNT') {
      setBankAccounts(prev => prev.filter(account => account.id !== accountId));
      if (editingAccount === accountId) {
        setEditingAccount(null);
        setAccountForm({});
      }
      return;
    }

    const account = bankAccounts.find(a => a.id === accountId);
    const accountLabel = account ? `${account.accountName} (${account.accountCurrency})` : accountId;

    setAlertModal({
      isOpen: true,
      title: t('admin.accounts.deleteConfirm.title', 'Delete Bank Account'),
      message: t('admin.accounts.deleteConfirm.message', 'Are you sure you want to delete "{{account}}"? This action cannot be undone.', { account: accountLabel }),
      type: 'warning',
      confirmLabel: t('common.delete', 'Delete'),
      onConfirm: async () => {
        setAlertModal(prev => ({ ...prev, isOpen: false }));
        const orgId = user?.profile?.organization?.id;
        if (!orgId) return;
        try {
          const response = await clientService.deleteBankAccount(orgId, accountId);
          if (response.success) {
            setBankAccounts(prev => prev.filter(account => account.id !== accountId));
            if (editingAccount === accountId) {
              setEditingAccount(null);
              setAccountForm({});
            }
          } else {
            setAlertModal({
              isOpen: true,
              title: t('common.error', 'Error'),
              message: t('admin.accounts.deleteError', 'Failed to delete bank account.'),
              type: 'error',
            });
          }
        } catch (error) {
          logger.error('Failed to delete bank account:', error);
          setAlertModal({
            isOpen: true,
            title: t('common.error', 'Error'),
            message: t('admin.accounts.deleteError', 'Failed to delete bank account.'),
            type: 'error',
          });
        }
      },
    });
  };

  const updateAccountForm = (field: keyof BankAccount, value: any) => {
    setAccountForm(prev => ({ ...prev, [field]: value }));

  };

  // Helper function to group and sort bank accounts
  const getSortedAndGroupedAccounts = () => {
    // Filter by account type (nostro/vostro) — always include NEW_ACCOUNT
    const filtered = bankAccounts.filter(a =>
      a.id === 'NEW_ACCOUNT' || (a.accountType || 'nostro') === accountTypeFilter
    );

    if (accountGrouping === 'none') {
      // Sort by active status first, then by account name
      const sorted = [...filtered].sort((a, b) => {
        // First by active status (active first)
        if (a.active !== b.active) return a.active ? -1 : 1;
        // Then by account name
        return a.accountName.localeCompare(b.accountName);
      });
      return sorted.map(account => ({ type: 'account' as const, data: account }));
    }

    // Group accounts by the selected criteria
    const groupKey = accountGrouping === 'bank' ? 'bankName'
      : accountGrouping === 'counterparty' ? 'counterpartyName'
      : 'accountCurrency';
    const grouped = filtered.reduce((groups, account) => {
      const key = account[groupKey];
      // Skip accounts with empty/null/undefined group keys
      if (!key || key.trim() === '') {
        return groups;
      }
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(account);
      return groups;
    }, {} as Record<string, BankAccount[]>);

    // Sort accounts within each group
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        // First by active status (active first)
        if (a.active !== b.active) return a.active ? -1 : 1;
        // Then by account name
        return a.accountName.localeCompare(b.accountName);
      });
    });

    // Build ordered list with proper grouping
    const orderedAccounts: Array<{ type: 'account' | 'header'; data: BankAccount | string }> = [];
    
    // Sort group keys and filter out empty groups
    const sortedGroupKeys = Object.keys(grouped)
      .filter(key => grouped[key].length > 0) // Only include groups with accounts
      .sort();
    
    sortedGroupKeys.forEach(groupName => {
      // Add header for this group
      orderedAccounts.push({ type: 'header', data: groupName });
      
      // Add all accounts in this group
      grouped[groupName].forEach(account => {
        orderedAccounts.push({ type: 'account', data: account });
      });
    });

    return orderedAccounts;
  };

  const saveConfiguration = async () => {
    if (!user?.profile?.organization?.id) {
      setAlertModal({
        isOpen: true,
        title: 'Error',
        message: 'No organization ID found',
        type: 'error'
      });
      return;
    }

    setIsSaving(true);
    
    try {
      // Save client settings (automation and alerts)
      await clientService.updateClientSettings(user.profile.organization.id, {
        automation: {
          ...automationSettings,
        },
        alerts: alertSettings
      });

      // Save expiry tolerance settings (if loaded)
      if (expiryTolerancesLoaded) {
        const tolResp = await clientService.updateExpiryTolerances(user.profile.organization.id, {
          currencyTolerances: expiryTolerances.currencyTolerances.filter(e => e.currency),
          defaultTolerance: expiryTolerances.defaultTolerance,
        });
        if (tolResp.success && tolResp.data) {
          setExpiryTolerances(tolResp.data);
          setTolTexts({});
        }
      }

      // Save expiry email config (if loaded)
      if (expiryEmailConfigLoaded) {
        const emailMap: Record<string, string> = {};
        for (const row of expiryEmailRows) {
          if (row.counterpartyId && row.emails.length > 0) {
            emailMap[row.counterpartyId] = row.emails.join('; ');
          }
        }
        const emailResp = await clientService.updateExpiryEmailConfig(user.profile.organization.id, {
          counterpartyExpiryEmails: emailMap,
          expiryEmailCc: expiryEmailCc.join('; '),
        });
        if (emailResp.success && emailResp.data) {
          const entries = Object.entries(emailResp.data.counterpartyExpiryEmails || {});
          setExpiryEmailRows(entries.map(([counterpartyId, emails]) => ({
            counterpartyId,
            emails: emails ? emails.split(';').map((e: string) => e.trim()).filter(Boolean) : []
          })));
          const ccStr = emailResp.data.expiryEmailCc || '';
          setExpiryEmailCc(ccStr ? ccStr.split(';').map((e: string) => e.trim()).filter(Boolean) : []);
        }
      }

      setAlertModal({
        isOpen: true,
        title: t('admin.modal.saveSuccess.title'),
        message: t('admin.modal.saveSuccess.message'),
        type: 'success'
      });
      
    } catch (error) {
      logger.error('Save failed:', error);
      setAlertModal({
        isOpen: true,
        title: t('admin.modal.error.title'),
        message: 'Failed to save configuration. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTabChange = (newTab: AdminTab) => {
    setActiveTab(newTab);
    loadDataForTab(newTab);
  };

  // Data Mapping functions
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processUploadedFile(file);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverUpload(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processUploadedFile(file);
    }
  };

  const processUploadedFile = (file: File) => {
    setUploadedFile(file);
    
    let extractedFields: string[] = [];
    let parsedData: any[] = [];
    
    if (file.name.toLowerCase().endsWith('.csv')) {
      // Parse CSV file
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length > 0) {
          // Extract headers from first line
          extractedFields = lines[0].split(',').map(field => field.trim().replace(/"/g, ''));
          
          // Parse first few rows for sample data
          for (let i = 1; i < Math.min(lines.length, 4); i++) {
            const values = lines[i].split(',').map(val => val.trim().replace(/"/g, ''));
            const row: any = {};
            extractedFields.forEach((field, index) => {
              row[field] = values[index] || '';
            });
            parsedData.push(row);
          }
        }
        updateFileData(extractedFields, parsedData, file);
      };
      reader.readAsText(file);
      
    } else if (file.name.toLowerCase().endsWith('.json')) {
      // Parse JSON file
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        try {
          const jsonData = JSON.parse(text);
          if (Array.isArray(jsonData) && jsonData.length > 0) {
            extractedFields = Object.keys(jsonData[0]);
            parsedData = jsonData.slice(0, 3);
          }
        } catch (error) {
          logger.error('Error parsing JSON:', error);
        }
        updateFileData(extractedFields, parsedData, file);
      };
      reader.readAsText(file);
      
    } else if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
      // Parse Excel file using exceljs library
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target?.result as ArrayBuffer;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(data);

          // Get the first worksheet
          const worksheet = workbook.worksheets[0];

          // Convert to JSON (array of arrays)
          const jsonData: any[][] = [];
          worksheet.eachRow((row: any, rowNumber: number) => {
            jsonData.push(row.values as any[]);
          });
          
          // Helper function to convert Excel date serial numbers to readable dates
          const convertExcelValue = (value: any, fieldName: string) => {
            // Only try to convert numbers that are likely to be dates based on field name
            const isLikelyDateField = fieldName && (
              fieldName.toLowerCase().includes('date') ||
              fieldName.toLowerCase().includes('maturity') ||
              fieldName.toLowerCase().includes('payment') ||
              fieldName.toLowerCase().includes('settlement') ||
              fieldName.toLowerCase().includes('fixing') ||
              fieldName.toLowerCase().includes('expiry') ||
              fieldName.toLowerCase().includes('value')
            );
            
            // Check if it's a number that could be an Excel date serial number
            if (isLikelyDateField && typeof value === 'number' && value > 25000 && value < 80000) {
              // Narrower range: 25000 ≈ 1968, 80000 ≈ 2119 (more realistic date range)
              try {
                // Excel stores dates as days since 1900-01-01 (with 1900 leap year bug)
                // Convert Excel serial number to JavaScript Date
                const excelEpoch = new Date(1899, 11, 30); // December 30, 1899 (accounting for Excel's leap year bug)
                const jsDate = new Date(excelEpoch.getTime() + (value * 24 * 60 * 60 * 1000));
                
                // Validate the date and check if it's reasonable
                if (jsDate && !isNaN(jsDate.getTime()) && jsDate.getFullYear() > 1968 && jsDate.getFullYear() < 2119) {
                  // Format as YYYY-MM-DD for consistency
                  return jsDate.toISOString().split('T')[0];
                }
              } catch (error) {
                // Date conversion failed
              }
            }
            // Return original value if not a date serial number or not a date field
            return value || '';
          };
          
          if (jsonData.length > 0) {
            // First row contains headers
            extractedFields = (jsonData[0] as any[]).map(header => String(header || '').trim());
            
            // Convert remaining rows to objects
            for (let i = 1; i < Math.min(jsonData.length, 4); i++) {
              const rowData = jsonData[i] as any[];
              const row: any = {};
              extractedFields.forEach((field, index) => {
                row[field] = convertExcelValue(rowData[index], field);
              });
              parsedData.push(row);
            }
          }
        } catch (error) {
          logger.error('Error parsing Excel file:', error);
        }
        updateFileData(extractedFields, parsedData, file);
      };
      reader.readAsArrayBuffer(file);
      
    } else {
      logger.warn('Unsupported file format:', file.name);
      updateFileData([], [], file);
    }
  };

  const updateFileData = (extractedFields: string[], parsedData: any[], file: File) => {
    setSourceFields(extractedFields);
    setUploadedData(parsedData);
    
    // Generate intelligent mapping suggestions based on actual field names
    if (extractedFields.length > 0) {
      const suggestedMapping = generateIntelligentMapping(extractedFields);
      setCurrentMapping(suggestedMapping);
      
      // Apply mapping to generate preview
      if (parsedData.length > 0) {
        const mapped = applyMapping(parsedData, suggestedMapping);
        setMappedData(mapped);
      }
    }
  };

  const generateMockData = (filename: string) => {
    // Mock data based on filename to simulate different client formats with comprehensive field set
    if (filename.toLowerCase().includes('bank')) {
      return [
        { 
          'ID_TRADE': 'T001', 'FECHA_TRADE': '15/01/2025', 'FECHA_VALOR': '17/01/2025', 'CONTRAPARTE': 'BANCO ABC', 
          'PRODUCTO': 'SPOT', 'COMPRA_VENTA': 'C', 'MONEDA1': 'USD', 'MONEDA2': 'CLP', 'MONTO': '100.000,50', 
          'TASA': '920,45', 'FECHA_PAGO': '17/01/2025', 'TIPO_LIQUIDACION': 'DVP', 'REFERENCIA': 'REF001',
          'COMENTARIOS': 'Trade normal', 'ESTADO': 'ACTIVO', 'USUARIO': 'admin', 'SISTEMA': 'CORE'
        },
        { 
          'ID_TRADE': 'T002', 'FECHA_TRADE': '16/01/2025', 'FECHA_VALOR': '18/01/2025', 'CONTRAPARTE': 'BANCO XYZ', 
          'PRODUCTO': 'FORWARD', 'COMPRA_VENTA': 'V', 'MONEDA1': 'EUR', 'MONEDA2': 'USD', 'MONTO': '50.000,00', 
          'TASA': '1,0856', 'FECHA_PAGO': '18/01/2025', 'TIPO_LIQUIDACION': 'NET', 'REFERENCIA': 'REF002',
          'COMENTARIOS': 'Forward EUR/USD', 'ESTADO': 'PENDIENTE', 'USUARIO': 'trader1', 'SISTEMA': 'FRONT'
        }
      ];
    }
    return [
      { 
        'TradeID': 'TR1001', 'TradeDate': '2025-01-15', 'ValueDate': '2025-01-17', 'Counterparty': 'Bank ABC', 
        'Product': 'FX SPOT', 'Ccy1': 'USD', 'Ccy2': 'CLP', 'Amount': '100000.50', 
        'Rate': '920.45', 'PaymentDate': '2025-01-17', 'SettlementType': 'DVP', 'Reference': 'REF001',
        'Comments': 'Standard trade', 'Status': 'ACTIVE', 'User': 'admin', 'Source': 'SYSTEM_A'
      },
      { 
        'TradeID': 'TR1002', 'TradeDate': '2025-01-16', 'ValueDate': '2025-01-18', 'Counterparty': 'Bank XYZ', 
        'Product': 'FX FORWARD', 'Ccy1': 'EUR', 'Ccy2': 'USD', 'Amount': '50000.00', 
        'Rate': '1.0856', 'PaymentDate': '2025-01-18', 'SettlementType': 'NET', 'Reference': 'REF002',
        'Comments': 'Forward EUR/USD', 'Status': 'PENDING', 'User': 'trader1', 'Source': 'SYSTEM_B'
      }
    ];
  };

  const generateIntelligentMapping = (sourceFields: string[]): FieldMapping[] => {
    const mappings: FieldMapping[] = [];
    
    sourceFields.forEach(sourceField => {
      const lowerField = sourceField.toLowerCase();
      
      // Intelligent field matching for ClientTradesGrid fields
      if (lowerField.includes('trade') && lowerField.includes('id') || lowerField === 'id_trade' || lowerField === 'tradeid') {
        mappings.push({ sourceField, targetField: 'tradeId', transformation: { type: 'direct' } });
      }
      else if (lowerField.includes('contraparte') || lowerField === 'counterparty') {
        mappings.push({ sourceField, targetField: 'counterparty', transformation: { type: 'direct' } });
      }
      else if (lowerField.includes('producto') || lowerField.includes('product type') || lowerField === 'producttype' || lowerField === 'product') {
        mappings.push({ sourceField, targetField: 'productType', transformation: { type: 'enum', params: { mapping: { 
          'SPOT': 'FX Spot', 
          'FORWARD': 'FX Forward', 
          'Forward': 'FX Forward',
          'forward': 'FX Forward',
          'FX SPOT': 'FX Spot', 
          'FX FORWARD': 'FX Forward', 
          'SWAP': 'FX Swap', 
          'OPTION': 'FX Option' 
        } } } });
      }
      else if (lowerField.includes('fecha') && lowerField.includes('trade') || lowerField === 'tradedate') {
        mappings.push({ sourceField, targetField: 'tradeDate', transformation: { type: 'format', params: { from: 'auto', to: 'YYYY-MM-DD' } } });
      }
      else if (lowerField.includes('valor') || lowerField.includes('value date') || lowerField === 'valuedate') {
        mappings.push({ sourceField, targetField: 'valueDate', transformation: { type: 'format', params: { from: 'auto', to: 'YYYY-MM-DD' } } });
      }
      else if (lowerField.includes('moneda1') || lowerField === 'ccy1' || lowerField === 'currency1' || lowerField.includes('currency 1')) {
        mappings.push({ sourceField, targetField: 'currency1', transformation: { type: 'enum', params: { mapping: { 
          'US$': 'USD',
          'USD': 'USD',
          '$': 'CLP',
          'CLP': 'CLP',
          'UF': 'CLF',
          'CLF': 'CLF',
          'EUR': 'EUR',
          '€': 'EUR',
          'GBP': 'GBP',
          '£': 'GBP'
        } } } });
      }
      else if (lowerField.includes('moneda2') || lowerField === 'ccy2' || lowerField === 'currency2' || lowerField.includes('currency 2')) {
        mappings.push({ sourceField, targetField: 'currency2', transformation: { type: 'enum', params: { mapping: { 
          'US$': 'USD',
          'USD': 'USD',
          '$': 'CLP',
          'CLP': 'CLP',
          'UF': 'CLF',
          'CLF': 'CLF',
          'EUR': 'EUR',
          '€': 'EUR',
          'GBP': 'GBP',
          '£': 'GBP'
        } } } });
      }
      else if (lowerField.includes('monto') || lowerField === 'amount') {
        mappings.push({ sourceField, targetField: 'amount', transformation: { type: 'format', params: { decimalSeparator: 'auto' } } });
      }
      else if (lowerField.includes('precio') || lowerField.includes('price') || lowerField.includes('tasa') || lowerField.includes('rate')) {
        mappings.push({ sourceField, targetField: 'price', transformation: { type: 'format', params: { decimalSeparator: 'auto', precision: 4 } } });
      }
      else if (lowerField.includes('pago') || lowerField === 'paymentdate' || (lowerField.includes('payment') && lowerField.includes('date'))) {
        mappings.push({ sourceField, targetField: 'paymentDate', transformation: { type: 'format', params: { from: 'auto', to: 'YYYY-MM-DD' } } });
      }
    });
    
    return mappings;
  };

  // Helper function to get suggested source field for each expected field
  const getSuggestedSourceField = (expectedFieldName: string): string => {
    const mapping = currentMapping.find(m => m.targetField === expectedFieldName);
    return mapping ? mapping.sourceField : 'No suggestion';
  };

  const applyMapping = (data: any[], mappings: FieldMapping[]) => {
    return data.map(row => {
      const mappedRow: any = {};
      
      mappings.forEach(mapping => {
        const sourceValue = row[mapping.sourceField];
        let targetValue = sourceValue;
        
        if (mapping.transformation) {
          switch (mapping.transformation.type) {
            case 'format':
              if (mapping.transformation.params?.decimalSeparator) {
                targetValue = sourceValue?.toString().replace(',', '.');
              }
              if (mapping.transformation.params?.from && mapping.transformation.params?.to) {
                // Date format conversion
                if (mapping.transformation.params.from === 'DD/MM/YYYY') {
                  const parts = sourceValue?.split('/');
                  if (parts?.length === 3) {
                    targetValue = `${parts[0]}${parts[1]}${parts[2]}`;
                  }
                }
              }
              break;
            case 'enum':
              const enumMapping = mapping.transformation.params?.mapping;
              if (enumMapping && enumMapping[sourceValue]) {
                targetValue = enumMapping[sourceValue];
              }
              break;
            case 'direct':
            default:
              targetValue = sourceValue;
          }
        }
        
        mappedRow[mapping.targetField] = targetValue;
      });
      
      return mappedRow;
    });
  };

  const updateMapping = (sourceField: string, targetField: string) => {
    setCurrentMapping(prev => {
      const updated = prev.filter(m => m.sourceField !== sourceField);
      if (targetField && targetField !== 'unmapped') {
        updated.push({ sourceField, targetField, transformation: { type: 'direct' } });
      }
      return updated;
    });
    
    // Reapply mapping to update preview
    if (uploadedData) {
      const newMapping = currentMapping.filter(m => m.sourceField !== sourceField);
      if (targetField && targetField !== 'unmapped') {
        newMapping.push({ sourceField, targetField, transformation: { type: 'direct' } });
      }
      const mapped = applyMapping(uploadedData, newMapping);
      setMappedData(mapped);
    }
  };

  const updateMappingByTarget = (targetField: string, sourceField: string) => {
    setCurrentMapping(prev => {
      // Remove any existing mapping for this target field
      const updated = prev.filter(m => m.targetField !== targetField);
      
      if (sourceField && sourceField !== 'unmapped') {
        // Also remove any mapping that was using this source field for a different target
        const finalUpdated = updated.filter(m => m.sourceField !== sourceField);
        
        // Find intelligent transformation for this mapping
        const transformation = getTransformationForField(sourceField, targetField);
        finalUpdated.push({ sourceField, targetField, transformation });
        
        return finalUpdated;
      }
      return updated;
    });
    
    // Reapply mapping to update preview
    if (uploadedData) {
      setTimeout(() => {
        const mapped = applyMapping(uploadedData, currentMapping);
        setMappedData(mapped);
      }, 0);
    }
  };

  const getTransformationForField = (sourceField: string, targetField: string) => {
    const lowerSourceField = sourceField.toLowerCase();
    
    // Date transformation
    if (targetField === 'tradeDate' || targetField === 'valueDate' || targetField === 'maturityDate') {
      const hasSlash = lowerSourceField.includes('/') || (uploadedData?.[0]?.[sourceField] && String(uploadedData[0][sourceField]).includes('/'));
      return { 
        type: 'format' as const, 
        params: hasSlash ? { from: 'DD/MM/YYYY', to: 'DDMMYYYY' } : { from: 'YYYY-MM-DD', to: 'DDMMYYYY' }
      };
    }
    
    // Amount/Rate transformation
    if (targetField === 'amount' || targetField === 'rate') {
      const hasComma = lowerSourceField.includes(',') || (uploadedData?.[0]?.[sourceField] && String(uploadedData[0][sourceField]).includes(','));
      return { 
        type: 'format' as const, 
        params: hasComma ? { decimalSeparator: ',' } : { decimalSeparator: '.' }
      };
    }
    
    // Enum transformations
    if (targetField === 'product') {
      return { type: 'enum' as const, params: { mapping: { 'SPOT': 'FX SPOT', 'FORWARD': 'FX FORWARD', 'SWAP': 'FX SWAP' } } };
    }
    
    if (targetField === 'buySell') {
      return { type: 'enum' as const, params: { mapping: { 'C': 'BUY', 'V': 'SELL', 'BUY': 'BUY', 'SELL': 'SELL' } } };
    }
    
    // Direct mapping for everything else
    return { type: 'direct' as const };
  };

  const saveMapping = () => {
    if (!mappingForm.name || !uploadedFile) return;
    
    const newMapping: DataMapping = {
      id: Date.now().toString(),
      name: mappingForm.name,
      description: mappingForm.description,
      fileType: uploadedFile.name.endsWith('.json') ? 'json' : uploadedFile.name.endsWith('.xlsx') ? 'excel' : 'csv',
      createdDate: new Date().toISOString().split('T')[0],
      fieldMappings: currentMapping,
      sampleData: mappedData?.slice(0, 3)
    };
    
    setDataMappings(prev => [...prev, newMapping]);
    setShowMappingForm(false);
    setMappingForm({name: '', description: ''});
    
    setAlertModal({
      isOpen: true,
      title: t('admin.dataMapping.saveMapping.successTitle'),
      message: t('admin.dataMapping.saveMapping.successMessage').replace('{name}', newMapping.name),
      type: 'success'
    });
  };

  const deleteMapping = (mappingId: string) => {
    setDataMappings(prev => prev.filter(m => m.id !== mappingId));
  };

  const clearUpload = () => {
    setUploadedFile(null);
    setUploadedData(null);
    setSourceFields([]);
    setCurrentMapping([]);
    setMappedData(null);
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <SystemDateDisplay clientId={user?.organization?.id || user?.id} />
      </div>
      <div className="admin-tabs">
        {visibleTabs.has('confirmations') && (
          <button
            className={`tab-button ${activeTab === 'confirmations' ? 'active' : ''}`}
            onClick={() => handleTabChange('confirmations')}
          >
            {t('admin.tabs.confirmations')}
          </button>
        )}
        {visibleTabs.has('accounts') && (
          <button
            className={`tab-button ${activeTab === 'accounts' ? 'active' : ''}`}
            onClick={() => handleTabChange('accounts')}
          >
            {t('admin.tabs.accounts')}
          </button>
        )}
        {visibleTabs.has('workflow') && (
          <button
            className={`tab-button ${activeTab === 'workflow' ? 'active' : ''}`}
            onClick={() => handleTabChange('workflow')}
          >
            {t('admin.tabs.workflow')}
          </button>
        )}
        {visibleTabs.has('fxgo') && (
          <button
            className={`tab-button ${activeTab === 'fxgo' ? 'active' : ''}`}
            onClick={() => handleTabChange('fxgo')}
          >
            FXGO
          </button>
        )}
        {visibleTabs.has('email') && (
          <button
            className={`tab-button ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => handleTabChange('email')}
          >
            {t('admin.tabs.email')}
          </button>
        )}
        {visibleTabs.has('accounting') && (
          <button
            className={`tab-button ${activeTab === 'accounting' ? 'active' : ''}`}
            onClick={() => handleTabChange('accounting')}
          >
            {t('admin.tabs.accounting')}
          </button>
        )}
        {visibleTabs.has('groupings') && (
          <button
            className={`tab-button ${activeTab === 'groupings' ? 'active' : ''}`}
            onClick={() => handleTabChange('groupings')}
          >
            {t('admin.tabs.groupings')}
          </button>
        )}
        {visibleTabs.has('imagecapture') && (
          <button
            className={`tab-button ${activeTab === 'imagecapture' ? 'active' : ''}`}
            onClick={() => handleTabChange('imagecapture')}
          >
            {t('admin.tabs.imagecapture')}
          </button>
        )}
        {visibleTabs.has('customdocuments') && (
          <button
            className={`tab-button ${activeTab === 'customdocuments' ? 'active' : ''}`}
            onClick={() => handleTabChange('customdocuments')}
          >
            {t('admin.tabs.customdocuments', 'Custom Documents')}
          </button>
        )}
        {visibleTabs.has('contracts') && (
          <button
            className={`tab-button ${activeTab === 'contracts' ? 'active' : ''}`}
            onClick={() => handleTabChange('contracts')}
          >
            {t('admin.tabs.contracts', 'Contracts')}
          </button>
        )}
        {visibleTabs.has('settlementinstructions') && (
          <button
            className={`tab-button ${activeTab === 'settlementinstructions' ? 'active' : ''}`}
            onClick={() => handleTabChange('settlementinstructions')}
          >
            {t('admin.tabs.settlementinstructions', 'Settlement Instructions')}
          </button>
        )}
        {visibleTabs.has('forwardexpiries') && (
          <button
            className={`tab-button ${activeTab === 'forwardexpiries' ? 'active' : ''}`}
            onClick={() => handleTabChange('forwardexpiries')}
          >
            {t('admin.tabs.forwardExpiries', 'Forward Expiries')}
          </button>
        )}
        {visibleTabs.has('users') && (
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => handleTabChange('users')}
          >
            {t('admin.tabs.users', 'Users')}
          </button>
        )}
        {visibleTabs.has('valuation') && (
          <button
            className={`tab-button ${activeTab === 'valuation' ? 'active' : ''}`}
            onClick={() => handleTabChange('valuation')}
          >
            {t('admin.tabs.valuation', 'Valuation')}
          </button>
        )}
        {visibleTabs.has('entityaliases') && (
          <button
            className={`tab-button ${activeTab === 'entityaliases' ? 'active' : ''}`}
            onClick={() => handleTabChange('entityaliases')}
          >
            {t('admin.tabs.entityAliases', 'Entity Aliases')}
          </button>
        )}

        <div className="admin-title">Admin</div>
      </div>
      
      <div className="admin-content">
        {activeTab === 'confirmations' && (
          clientSettingsLoading || !clientSettingsLoaded ? (
            <div className="tab-loading">
              <div className="spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : (
          <div className="automation-settings">
            <div className="automation-grid">
              {/* Column 1: Confirmation Comparison Fields Settings */}
              <div className="automation-column">
                <div className="setting-card comparison-fields-wrapper">
                  <ConfirmationComparisonFieldsSettings availableBanks={availableBanks} />
                </div>
              </div>

              {/* Column 2: Auto-confirm settings */}
              <div className="automation-column">
                {/* Auto-confirm Matched */}
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>{t('admin.automation.autoConfirmMatched.title')}</h3>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={automationSettings.autoConfirmMatched.enabled}
                        onChange={(e) => handleAutomationChange('autoConfirmMatched', {
                          ...automationSettings.autoConfirmMatched,
                          enabled: e.target.checked
                        })}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p>{t('admin.automation.autoConfirmMatched.description')}</p>

                  {/* Auto Settlement Instruction — commented out for now
                  <div className="nested-setting">
                    <div className="setting-header nested">
                      <h4>{t('admin.automation.autoCartaInstruccion.title')}</h4>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={automationSettings.autoCartaInstruccion}
                          disabled={!automationSettings.autoConfirmMatched.enabled}
                          onChange={(e) => handleAutomationChange('autoCartaInstruccion', e.target.checked)}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                    <p className="nested-description">{t('admin.automation.autoCartaInstruccion.description')}</p>
                    {automationSettings.autoCartaInstruccion && (
                      <div className="warning-message">
                        <span className="warning-icon">⚠️</span>
                        <span>{t('admin.automation.autoCartaInstruccion.warningMessage')}</span>
                      </div>
                    )}
                  </div>
                  */}
                </div>

                {/* NEW: Auto-confirm workflow on Confirmation OK */}
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>{t('admin.automation.autoConfirmOnMatch.title')}</h3>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={automationSettings.autoConfirmOnMatch}
                        onChange={(e) => handleAutomationChange('autoConfirmOnMatch', e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p>{t('admin.automation.autoConfirmOnMatch.description')}</p>
                </div>

                {/* Auto-confirm Disputed */}
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>{t('admin.automation.autoConfirmDisputed.title')}</h3>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={automationSettings.autoConfirmDisputed.enabled}
                        onChange={(e) => handleAutomationChange('autoConfirmDisputed', {
                          ...automationSettings.autoConfirmDisputed,
                          enabled: e.target.checked
                        })}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p>{t('admin.automation.autoConfirmDisputed.description')}</p>
                </div>
              </div>

              {/* Column 3: Alerts for matched + disputed trades */}
              <div className="automation-column alerts-column">
                {/* Email alerts — Matched trades */}
                <div className="alert-section">
                  <h3>{t('admin.alerts.emailConfirmed')}</h3>
                  <div className="alert-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={alertSettings.emailConfirmedTrades.enabled}
                        onChange={(e) => {
                          setAlertSettings(prev => ({
                            ...prev,
                            emailConfirmedTrades: { ...prev.emailConfirmedTrades, enabled: e.target.checked }
                          }));
                      
                        }}
                      />
                      <span className="slider"></span>
                    </label>
                    <span>{t('admin.alerts.enableEmail')}</span>
                  </div>
                  {alertSettings.emailConfirmedTrades.enabled && (
                    <div className="contact-list">
                      <div className="add-contact">
                        <input type="email" placeholder={t('admin.alerts.addEmail')} value={newEmailConfirmed} onChange={(e) => setNewEmailConfirmed(e.target.value)} />
                        <button onClick={() => addEmail('emailConfirmedTrades')}>{t('admin.alerts.addButton')}</button>
                      </div>
                      <div className="contact-items">
                        {alertSettings.emailConfirmedTrades.emails.map((email, index) => (
                          <div key={index} className="contact-item"><span>{email}</span><button onClick={() => removeEmail('emailConfirmedTrades', email)}>×</button></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* SMS alerts — Matched trades */}
                <div className="alert-section">
                  <h3>{t('admin.alerts.smsConfirmed')}</h3>
                  <div className="alert-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={alertSettings.smsConfirmedTrades.enabled}
                        onChange={(e) => {
                          setAlertSettings(prev => ({
                            ...prev,
                            smsConfirmedTrades: { ...prev.smsConfirmedTrades, enabled: e.target.checked }
                          }));
                      
                        }}
                      />
                      <span className="slider"></span>
                    </label>
                    <span>{t('admin.alerts.enableSms')}</span>
                  </div>
                  {alertSettings.smsConfirmedTrades.enabled && (
                    <div className="contact-list">
                      <div className="add-contact">
                        <input type="tel" placeholder={t('admin.alerts.addPhone')} value={newPhoneConfirmed} onChange={(e) => setNewPhoneConfirmed(e.target.value)} />
                        <button onClick={() => addPhone('smsConfirmedTrades')}>{t('admin.alerts.addButton')}</button>
                      </div>
                      <div className="contact-items">
                        {alertSettings.smsConfirmedTrades.phones.map((phone, index) => (
                          <div key={index} className="contact-item"><span>{phone}</span><button onClick={() => removePhone('smsConfirmedTrades', phone)}>×</button></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="alerts-divider" />

                {/* Email alerts — Disputed trades */}
                <div className="alert-section">
                  <h3>{t('admin.alerts.emailDisputed')}</h3>
                  <div className="alert-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={alertSettings.emailDisputedTrades.enabled}
                        onChange={(e) => {
                          setAlertSettings(prev => ({
                            ...prev,
                            emailDisputedTrades: { ...prev.emailDisputedTrades, enabled: e.target.checked }
                          }));
                      
                        }}
                      />
                      <span className="slider"></span>
                    </label>
                    <span>{t('admin.alerts.enableEmail')}</span>
                  </div>
                  {alertSettings.emailDisputedTrades.enabled && (
                    <div className="contact-list">
                      <div className="add-contact">
                        <input type="email" placeholder={t('admin.alerts.addEmail')} value={newEmailDisputed} onChange={(e) => setNewEmailDisputed(e.target.value)} />
                        <button onClick={() => addEmail('emailDisputedTrades')}>{t('admin.alerts.addButton')}</button>
                      </div>
                      <div className="contact-items">
                        {alertSettings.emailDisputedTrades.emails.map((email, index) => (
                          <div key={index} className="contact-item"><span>{email}</span><button onClick={() => removeEmail('emailDisputedTrades', email)}>×</button></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* SMS alerts — Disputed trades */}
                <div className="alert-section">
                  <h3>{t('admin.alerts.smsDisputed')}</h3>
                  <div className="alert-toggle">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={alertSettings.smsDisputedTrades.enabled}
                        onChange={(e) => {
                          setAlertSettings(prev => ({
                            ...prev,
                            smsDisputedTrades: { ...prev.smsDisputedTrades, enabled: e.target.checked }
                          }));
                      
                        }}
                      />
                      <span className="slider"></span>
                    </label>
                    <span>{t('admin.alerts.enableSms')}</span>
                  </div>
                  {alertSettings.smsDisputedTrades.enabled && (
                    <div className="contact-list">
                      <div className="add-contact">
                        <input type="tel" placeholder={t('admin.alerts.addPhone')} value={newPhoneDisputed} onChange={(e) => setNewPhoneDisputed(e.target.value)} />
                        <button onClick={() => addPhone('smsDisputedTrades')}>{t('admin.alerts.addButton')}</button>
                      </div>
                      <div className="contact-items">
                        {alertSettings.smsDisputedTrades.phones.map((phone, index) => (
                          <div key={index} className="contact-item"><span>{phone}</span><button onClick={() => removePhone('smsDisputedTrades', phone)}>×</button></div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Save button — at bottom of alerts column */}
                <div className="confirmations-save-actions">
                  <button
                    className="btn-primary"
                    onClick={saveConfiguration}
                    disabled={isSaving}
                  >
                    {isSaving ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          )
        )}

        {activeTab === 'accounts' && (
          bankAccountsLoading || !bankAccountsLoaded ? (
            <div className="tab-loading">
              <div className="spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : (
          <div className="accounts-settings">
            <div className="accounts-header">
              <div className="settlement-header">
                <div>
                  <h2>{t('admin.accounts.title')}</h2>
                  <p className="accounts-description">{t('admin.accounts.description')}</p>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  {/* Nostro / Vostro toggle */}
                  <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                    <button
                      onClick={() => setAccountTypeFilter('nostro')}
                      style={{
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        border: 'none',
                        cursor: 'pointer',
                        background: accountTypeFilter === 'nostro' ? 'var(--accent-blue)' : 'var(--bg-primary)',
                        color: accountTypeFilter === 'nostro' ? '#fff' : 'var(--text-secondary)',
                      }}
                    >
                      {t('admin.accounts.nostro', 'Nuestras Cuentas')}
                    </button>
                    <button
                      onClick={() => setAccountTypeFilter('vostro')}
                      style={{
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        border: 'none',
                        borderLeft: '1px solid var(--border-color)',
                        cursor: 'pointer',
                        background: accountTypeFilter === 'vostro' ? 'var(--accent-blue)' : 'var(--bg-primary)',
                        color: accountTypeFilter === 'vostro' ? '#fff' : 'var(--text-secondary)',
                      }}
                    >
                      {t('admin.accounts.vostro', 'Cuentas de Contraparte')}
                    </button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                      {t('admin.accounts.groupBy')}:
                    </label>
                    <select
                      value={accountGrouping}
                      onChange={(e) => setAccountGrouping(e.target.value as 'none' | 'bank' | 'currency' | 'counterparty')}
                      style={{
                        background: 'var(--bg-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '0.5rem',
                        color: 'var(--text-primary)',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="none">{t('admin.accounts.noGrouping')}</option>
                      <option value="bank">{t('admin.accounts.groupByBank')}</option>
                      <option value="currency">{t('admin.accounts.groupByCurrency')}</option>
                      {accountTypeFilter === 'vostro' && (
                        <option value="counterparty">{t('admin.accounts.groupByCounterparty', 'Contraparte')}</option>
                      )}
                    </select>
                  </div>
                  <button className="add-account-button" onClick={handleAddAccount}>
                    + {t('admin.accounts.addAccount')}
                  </button>
                </div>
              </div>
            </div>

            <div className={`accounts-table${accountTypeFilter === 'vostro' ? ' vostro-view' : ''}`}>
              <div className="table-header">
                <div className="header-cell accounts-active-header">{t('admin.accounts.table.active')}</div>
                {accountTypeFilter === 'vostro' && (
                  <div className="header-cell">{t('admin.accounts.table.counterparty', 'Contraparte')}</div>
                )}
                <div className="header-cell">{t('admin.accounts.table.accountName')}</div>
                <div className="header-cell">{t('admin.accounts.table.bankName')}</div>
                <div className="header-cell">{t('admin.accounts.table.swiftCode')}</div>
                <div className="header-cell">{t('admin.accounts.table.accountCurrency')}</div>
                <div className="header-cell">{t('admin.accounts.table.accountNumber')}</div>
                <div className="header-cell">{t('admin.accounts.table.actions')}</div>
              </div>
              
              {(() => {
                const orderedAccounts = getSortedAndGroupedAccounts();
                
                const renderAccount = (account: BankAccount) => (
                  <div key={account.id} className={`table-row ${!account.active ? 'inactive' : ''}`}>
                    {editingAccount === account.id ? (
                      // Edit mode
                      <>
                        <div className="table-cell center">
                          <input
                            type="checkbox"
                            checked={accountForm.active || false}
                            onChange={(e) => updateAccountForm('active', e.target.checked)}
                          />
                        </div>
                        {accountTypeFilter === 'vostro' && (
                          <div className="table-cell">
                            <select
                              value={accountForm.counterpartyId || ''}
                              onChange={(e) => {
                                const selectedBank = availableBanks.find(b => b.id === e.target.value);
                                updateAccountForm('counterpartyId', e.target.value || undefined);
                                updateAccountForm('counterpartyName', selectedBank?.name || undefined);
                              }}
                            >
                              <option value="">{t('admin.accounts.placeholders.counterparty', '-- Contraparte --')}</option>
                              {availableBanks.filter(b => b.isCounterparty).map(bank => (
                                <option key={bank.id} value={bank.id}>{bank.name}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="table-cell">
                          <input
                            type="text"
                            value={accountForm.accountName || ''}
                            onChange={(e) => updateAccountForm('accountName', e.target.value)}
                            placeholder={t('admin.accounts.placeholders.accountName')}
                          />
                        </div>
                        <div className="table-cell">
                          <select
                            value={accountForm.bankName || ''}
                            onChange={(e) => {
                              const selectedName = e.target.value;
                              updateAccountForm('bankName', selectedName);
                              const bank = availableBanks.find(b => b.name === selectedName);
                              if (bank?.swiftCode) {
                                updateAccountForm('swiftCode', bank.swiftCode);
                              } else {
                                updateAccountForm('swiftCode', '');
                              }
                            }}
                          >
                            <option value="">{t('admin.accounts.placeholders.bankName')}</option>
                            {availableBanks.map(bank => (
                              <option key={bank.id} value={bank.name}>{bank.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="table-cell">
                          <input
                            type="text"
                            value={accountForm.swiftCode || ''}
                            readOnly
                            placeholder={t('admin.accounts.placeholders.swiftCode')}
                            style={{ opacity: 0.7, cursor: 'default' }}
                          />
                        </div>
                        <div className="table-cell">
                          <select
                            value={accountForm.accountCurrency || ''}
                            onChange={(e) => updateAccountForm('accountCurrency', e.target.value)}
                          >
                            <option value="">{t('admin.accounts.placeholders.accountCurrency')}</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="CLP">CLP</option>
                            <option value="GBP">GBP</option>
                            <option value="JPY">JPY</option>
                          </select>
                        </div>
                        <div className="table-cell">
                          <input
                            type="text"
                            value={accountForm.accountNumber || ''}
                            onChange={(e) => updateAccountForm('accountNumber', e.target.value)}
                            placeholder={t('admin.accounts.placeholders.accountNumber')}
                          />
                        </div>
                        <div className="table-cell actions">
                          <button 
                            className="save-button" 
                            onClick={handleSaveAccount}
                            disabled={isSavingAccount}
                            title={isSavingAccount ? t('admin.accounts.saving') : t('admin.accounts.save')}
                          >
                            {isSavingAccount ? '⏳' : '✓'}
                          </button>
                          <button 
                            className="cancel-button" 
                            onClick={handleCancelAccount}
                            disabled={isSavingAccount}
                          >
                            ✗
                          </button>
                        </div>
                      </>
                    ) : (
                      // View mode
                      <>
                        <div className="table-cell center">
                          <input
                            type="checkbox"
                            checked={account.active}
                            readOnly
                          />
                        </div>
                        {accountTypeFilter === 'vostro' && (
                          <div className="table-cell">{account.counterpartyName || '—'}</div>
                        )}
                        <div className="table-cell">{account.accountName}</div>
                        <div className="table-cell">{getBankDisplayName(account.bankName)}</div>
                        <div className="table-cell">{account.swiftCode}</div>
                        <div className="table-cell">{account.accountCurrency}</div>
                        <div className="table-cell">{account.accountNumber}</div>
                        <div className="table-cell actions">
                          <button className="edit-button" onClick={() => handleEditAccount(account)}>✏️</button>
                          <button className="delete-button" onClick={() => handleDeleteAccount(account.id)}>🗑️</button>
                        </div>
                      </>
                    )}
                  </div>
                );

                return orderedAccounts.map((item, index) => {
                  if (item.type === 'header') {
                    const headerText = item.data as string;
                    return (
                      <div key={`header-${headerText}-${index}`} className="counterparty-group-header">
                        <h4>{headerText}</h4>
                      </div>
                    );
                  } else {
                    return renderAccount(item.data as BankAccount);
                  }
                });
              })()}
              
              {bankAccounts.filter(a => (a.accountType || 'nostro') === accountTypeFilter).length === 0 && (
                <div className="empty-state">
                  <p>{accountTypeFilter === 'vostro'
                    ? t('admin.accounts.emptyVostro', 'No counterparty accounts configured yet. Click "Add Account" to register a counterparty\'s bank account.')
                    : t('admin.accounts.emptyNostro', 'No bank accounts configured yet. Click "Add Account" to create your first account.')
                  }</p>
                </div>
              )}
            </div>
          </div>
          )
        )}

        {activeTab === 'mapping' && (
          <div className="mapping-settings">
            <h2>{t('admin.dataMapping.title')}</h2>
            <p className="mapping-description">
              {t('admin.dataMapping.description')}
            </p>
            
            {/* File Upload Section */}
            {!uploadedFile ? (
              <div className="file-upload-section">
                <h3>{t('admin.dataMapping.uploadSampleFile')}</h3>
                <div 
                  className={`file-upload-area ${dragOverUpload ? 'drag-over' : ''}`}
                  onDragOver={(e) => { e.preventDefault(); setDragOverUpload(true); }}
                  onDragLeave={() => setDragOverUpload(false)}
                  onDrop={handleFileDrop}
                >
                  <div className="upload-content">
                    <div className="upload-icon">📁</div>
                    <p>{t('admin.dataMapping.dragDropText')}</p>
                    <label className="upload-button">
                      {t('admin.dataMapping.browseFiles')}
                      <input 
                        type="file" 
                        accept=".csv,.xlsx,.xls,.json" 
                        onChange={handleFileUpload}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <p className="file-types">{t('admin.dataMapping.supportedFormats')}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mapping-interface">
                <div className="uploaded-file-info">
                  <h3>File: {uploadedFile.name}</h3>
                  <button className="clear-upload-button" onClick={clearUpload}>
                    ✗ {t('admin.dataMapping.clearUpload')}
                  </button>
                </div>

                {/* Field Mapping */}
                <div className="field-mapping-section">
                  <h4>{t('admin.dataMapping.fieldMapping')}</h4>
                  <div className="mapping-columns">
                    <div className="mapping-headers">
                      <div className="expected-field-header">{t('admin.dataMapping.headers.expectedField')}</div>
                      <div className="source-field-header">{t('admin.dataMapping.headers.sourceField')}</div>
                      <div className="transformation-header">{t('admin.dataMapping.headers.transformation')}</div>
                      <div className="example-header">{t('admin.dataMapping.headers.example')}</div>
                    </div>
                    {EXPECTED_FIELDS.map(expectedField => (
                      <div key={expectedField.name} className="mapping-row">
                        <div className="expected-field-column">
                          <div className="field-name">{t(`grid.columns.${expectedField.name}`)}</div>
                          <div className="field-format">{expectedField.format}</div>
                          <div className="field-description">{t(`admin.dataMapping.fieldDescriptions.${expectedField.name}`)}</div>
                        </div>
                        <div className="source-field-column">
                          <select 
                            className="source-field-dropdown"
                            value={getSuggestedSourceField(expectedField.name) === 'No suggestion' ? '' : getSuggestedSourceField(expectedField.name)}
                            onChange={(e) => updateMappingByTarget(expectedField.name, e.target.value)}
                          >
                            <option value="">{t('admin.dataMapping.dropdown.selectField')}</option>
                            {(() => {
                              return sourceFields.map(sourceField => (
                                <option key={sourceField} value={sourceField}>
                                  {sourceField}
                                </option>
                              ));
                            })()}
                          </select>
                        </div>
                        <div className="transformation-column">
                          <div className="transformation-info">
                            {(() => {
                              const mapping = currentMapping.find(m => m.targetField === expectedField.name);
                              if (!mapping) return <span className="no-transformation">-</span>;
                              
                              const { type, params } = mapping.transformation || {};
                              switch (type) {
                                case 'direct':
                                  return <span className="transform-direct">{t('admin.dataMapping.transformations.directCopy')}</span>;
                                case 'format':
                                  if (params?.from && params?.to) {
                                    return <span className="transform-format">{t('admin.dataMapping.transformations.date')}: {params.from} → {params.to}</span>;
                                  }
                                  if (params?.decimalSeparator) {
                                    return <span className="transform-format">{t('admin.dataMapping.transformations.decimal')}: {params.decimalSeparator === 'auto' ? t('admin.dataMapping.transformations.autoDetect') : params.decimalSeparator}</span>;
                                  }
                                  if (params?.precision) {
                                    return <span className="transform-format">{t('admin.dataMapping.transformations.precision')}: {params.precision} {t('admin.dataMapping.transformations.decimals')}</span>;
                                  }
                                  return <span className="transform-format">{t('admin.dataMapping.transformations.formatConversion')}</span>;
                                case 'enum':
                                  const mappingCount = params?.mapping ? Object.keys(params.mapping).length : 0;
                                  const mappingPreview = params?.mapping ? Object.entries(params.mapping).slice(0, 2).map(([k, v]) => `${k}→${v}`).join(', ') : '';
                                  return <span className="transform-enum">{t('admin.dataMapping.transformations.enum')}: {mappingPreview}{mappingCount > 2 ? '...' : ''}</span>;
                                default:
                                  return <span className="transform-unknown">{t('admin.dataMapping.transformations.unknown')}</span>;
                              }
                            })()}
                          </div>
                        </div>
                        <div className="example-column">
                          <div className="example-data">
                            {(() => {
                              const mapping = currentMapping.find(m => m.targetField === expectedField.name);
                              if (!mapping || !uploadedData?.[0]) {
                                return <span className="no-example">{t('admin.dataMapping.examples.none')}</span>;
                              }
                              
                              const sourceValue = uploadedData[0][mapping.sourceField];
                              if (!sourceValue) {
                                return <span className="no-example">{t('admin.dataMapping.examples.noData')}</span>;
                              }
                              
                              // Apply the same transformation logic as in applyMapping
                              let transformedValue = sourceValue;
                              const { type, params } = mapping.transformation || {};
                              
                              switch (type) {
                                case 'format':
                                  if (params?.decimalSeparator === ',' || String(sourceValue).includes(',')) {
                                    transformedValue = String(sourceValue).replace(',', '.');
                                  }
                                  if (params?.from === 'DD/MM/YYYY' && String(sourceValue).includes('/')) {
                                    const parts = String(sourceValue).split('/');
                                    if (parts.length === 3) {
                                      transformedValue = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                                    }
                                  }
                                  break;
                                case 'enum':
                                  const enumMapping = params?.mapping;
                                  if (enumMapping && enumMapping[sourceValue]) {
                                    transformedValue = enumMapping[sourceValue];
                                  }
                                  break;
                                case 'direct':
                                default:
                                  transformedValue = sourceValue;
                              }
                              
                              return (
                                <div className="example-transformation">
                                  <div className="source-value">{String(sourceValue)}</div>
                                  <div className="arrow">→</div>
                                  <div className="target-value">{String(transformedValue)}</div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview Section */}
                {mappedData && mappedData.length > 0 && (
                  <div className="preview-section">
                    <div className="preview-header-row">
                      <h4>{t('admin.dataMapping.mappingPreview')}</h4>
                      <button 
                        className="refresh-preview-button"
                        onClick={() => {
                          if (uploadedData && uploadedData.length > 0) {
                            const mapped = applyMapping(uploadedData, currentMapping);
                            setMappedData(mapped);
                          }
                        }}
                        title={t('admin.dataMapping.refreshPreview')}
                      >
                        🔄 {t('admin.dataMapping.refresh')}
                      </button>
                    </div>
                    <div className="preview-table-container">
                      <table className="preview-data-table">
                        <thead>
                          <tr>
                            {EXPECTED_FIELDS.map(field => (
                              <th key={field.name} className="preview-th">
                                {t(`grid.columns.${field.name}`)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {mappedData.slice(0, 3).map((row, index) => (
                            <tr key={index}>
                              {EXPECTED_FIELDS.map(field => (
                                <td key={field.name} className="preview-td">
                                  {row[field.name] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Save Mapping Section */}
                <div className="save-mapping-section">
                  {!showMappingForm ? (
                    <button 
                      className="show-save-form-button"
                      onClick={() => setShowMappingForm(true)}
                    >
                      {t('admin.dataMapping.saveMapping.showSaveButton')}
                    </button>
                  ) : (
                    <div className="save-mapping-form">
                      <h4>{t('admin.dataMapping.saveMapping.title')}</h4>
                      <div className="form-grid">
                        <div className="form-group">
                          <label>{t('admin.dataMapping.saveMapping.nameLabel')}</label>
                          <input
                            type="text"
                            value={mappingForm.name}
                            onChange={(e) => setMappingForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={t('admin.dataMapping.saveMapping.namePlaceholder')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('admin.dataMapping.saveMapping.descriptionLabel')}</label>
                          <input
                            type="text"
                            value={mappingForm.description}
                            onChange={(e) => setMappingForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder={t('admin.dataMapping.saveMapping.descriptionPlaceholder')}
                          />
                        </div>
                      </div>
                      <div className="form-actions">
                        <button 
                          className="save-mapping-button" 
                          onClick={saveMapping}
                          disabled={!mappingForm.name}
                        >
                          {t('admin.dataMapping.saveMapping.saveButton')}
                        </button>
                        <button 
                          className="cancel-mapping-button"
                          onClick={() => setShowMappingForm(false)}
                        >
                          {t('admin.dataMapping.saveMapping.cancelButton')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Saved Mappings Section */}
            <div className="saved-mappings-section">
              <h3>{t('admin.dataMapping.savedMappings.title')}</h3>
              {dataMappings.length === 0 ? (
                <div className="empty-state">
                  <p>{t('admin.dataMapping.savedMappings.emptyState')}</p>
                </div>
              ) : (
                <div className="mappings-table">
                  <div className="mappings-header">
                    <div>{t('admin.dataMapping.savedMappings.tableHeaders.name')}</div>
                    <div>{t('admin.dataMapping.savedMappings.tableHeaders.description')}</div>
                    <div>{t('admin.dataMapping.savedMappings.tableHeaders.fileType')}</div>
                    <div>{t('admin.dataMapping.savedMappings.tableHeaders.created')}</div>
                    <div>{t('admin.dataMapping.savedMappings.tableHeaders.fields')}</div>
                    <div>{t('admin.dataMapping.savedMappings.tableHeaders.actions')}</div>
                  </div>
                  {dataMappings.map(mapping => (
                    <div key={mapping.id} className="mapping-row">
                      <div className="mapping-name">{mapping.name}</div>
                      <div className="mapping-description">{mapping.description}</div>
                      <div className="mapping-file-type">
                        <span className={`file-type-badge ${mapping.fileType}`}>
                          {mapping.fileType.toUpperCase()}
                        </span>
                      </div>
                      <div className="mapping-date">{mapping.createdDate}</div>
                      <div className="mapping-fields">
                        {mapping.fieldMappings.length} {t('admin.dataMapping.savedMappings.fieldsCount')}
                      </div>
                      <div className="mapping-actions">
                        <button className="edit-button" title={t('admin.dataMapping.savedMappings.tooltips.edit')}>✏️</button>
                        <button 
                          className="delete-button" 
                          onClick={() => deleteMapping(mapping.id)}
                          title={t('admin.dataMapping.savedMappings.tooltips.delete')}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'workflow' && (
          <div className="workflow-settings">
            <WorkflowConfigPanel />
          </div>
        )}

        {activeTab === 'fxgo' && (
          fxgoSettingsLoading || !fxgoSettingsLoaded ? (
            <div className="tab-loading">
              <div className="spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : (
          <div className="fxgo-settings-wrapper">
            <FXGOSettings
              settings={fxgoSettings}
              saving={isSavingFxgo}
              onSave={async (data) => {
                if (!user?.profile?.organization?.id) return;
                setIsSavingFxgo(true);
                try {
                  const response = await clientService.updateFXGOSettings(
                    user.profile.organization.id,
                    data
                  );
                  if (response.success && response.data) {
                    setFxgoSettings(response.data as unknown as FXGODefaultsSettings);
                    setAlertModal({
                      isOpen: true,
                      title: t('admin.fxgo.saveSuccess', 'FXGO Settings Saved'),
                      message: t('admin.fxgo.saveSuccessMessage', 'FXGO default settings have been saved successfully.'),
                      type: 'success'
                    });
                  } else {
                    setAlertModal({
                      isOpen: true,
                      title: t('admin.fxgo.saveError', 'Error'),
                      message: t('admin.fxgo.saveErrorMessage', 'Failed to save FXGO settings.'),
                      type: 'error'
                    });
                  }
                } catch (error) {
                  logger.error('Failed to save FXGO settings:', error);
                  setAlertModal({
                    isOpen: true,
                    title: t('admin.fxgo.saveError', 'Error'),
                    message: t('admin.fxgo.saveErrorMessage', 'Failed to save FXGO settings.'),
                    type: 'error'
                  });
                } finally {
                  setIsSavingFxgo(false);
                }
              }}
            />
          </div>
          )
        )}

        {activeTab === 'email' && (
          <EmailConnectionSettings
            onToast={(message, type) => {
              setAlertModal({
                isOpen: true,
                title: type === 'success' ? t('common.success') : t('common.error'),
                message,
                type: type === 'success' ? 'success' : 'error',
              });
            }}
          />
        )}

        {activeTab === 'accounting' && (
          <AccountingConfigPanel />
        )}

        {activeTab === 'groupings' && (
          <GroupingsConfigPanel />
        )}

        {activeTab === 'imagecapture' && (
          <TradeImageCaptureConfigPanel />
        )}

        {activeTab === 'customdocuments' && (
          <CustomDocumentsConfigPanel />
        )}

        {activeTab === 'contracts' && (
          <ContractsAdminPanel />
        )}

        {activeTab === 'settlementinstructions' && (
          <SettlementInstructionsAdminPanel />
        )}

        {activeTab === 'forwardexpiries' && (
          clientSettingsLoading || !clientSettingsLoaded ? (
            <div className="tab-loading">
              <div className="spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : (
          <div className="automation-settings">
            <div className="automation-grid">
              <div className="automation-column">
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>{t('admin.automation.autoExpiryResponse.title')}</h3>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={automationSettings.autoExpiryResponse}
                        onChange={(e) => handleAutomationChange('autoExpiryResponse', e.target.checked)}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                  <p>{t('admin.automation.autoExpiryResponse.description')}</p>
                </div>
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>{t('admin.expiryTolerances.currency', 'Currency')} / {t('admin.expiryTolerances.tolerance', 'Tolerance')}</h3>
                  </div>
                  <p>{t('admin.expiryTolerances.description', 'Defines how much the counterparty\'s settlement amount can differ from ours before flagging a discrepancy.')}</p>

                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.75rem', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t('admin.expiryTolerances.currency', 'Currency')}
                        </th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                          {t('admin.expiryTolerances.tolerance', 'Tolerance')}
                        </th>
                        <th style={{ width: '30px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {expiryTolerances.currencyTolerances.map((entry, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '4px 8px' }}>
                            <input
                              type="text"
                              maxLength={3}
                              value={entry.currency}
                              onChange={(e) => {
                                const updated = [...expiryTolerances.currencyTolerances];
                                updated[idx] = { ...updated[idx], currency: e.target.value.toUpperCase() };
                                setExpiryTolerances(prev => ({ ...prev, currencyTolerances: updated }));
                              }}
                              style={{
                                width: '70px', padding: '5px 8px', fontSize: '0.85rem',
                                border: '1px solid var(--border-color)', borderRadius: '4px',
                                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                                textTransform: 'uppercase',
                              }}
                            />
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={tolTexts[`row-${idx}`] ?? String(entry.tolerance)}
                              onChange={(e) => {
                                if (/^[\d.,]*$/.test(e.target.value)) {
                                  setTolTexts(prev => ({ ...prev, [`row-${idx}`]: e.target.value }));
                                }
                              }}
                              onBlur={(e) => {
                                const raw = e.target.value.replace(',', '.');
                                const parsed = parseFloat(raw);
                                const updated = [...expiryTolerances.currencyTolerances];
                                updated[idx] = { ...updated[idx], tolerance: isNaN(parsed) ? 0 : parsed };
                                setExpiryTolerances(prev => ({ ...prev, currencyTolerances: updated }));
                                setTolTexts(prev => ({ ...prev, [`row-${idx}`]: isNaN(parsed) ? '0' : String(parsed) }));
                              }}
                              style={{
                                width: '100px', padding: '5px 8px', fontSize: '0.85rem',
                                border: '1px solid var(--border-color)', borderRadius: '4px',
                                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                              }}
                            />
                          </td>
                          <td style={{ padding: '4px 0', textAlign: 'center' }}>
                            <button
                              onClick={() => {
                                const updated = expiryTolerances.currencyTolerances.filter((_, i) => i !== idx);
                                setExpiryTolerances(prev => ({ ...prev, currencyTolerances: updated }));
                              }}
                              title={t('admin.expiryTolerances.removeCurrency', 'Remove')}
                              style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', fontSize: '1rem', padding: '0.25rem', lineHeight: 1 }}
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}

                      {/* "Others" row — the default tolerance for all unlisted currencies */}
                      <tr style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-tertiary)' }}>
                        <td style={{ padding: '4px 8px' }}>
                          <span style={{ display: 'inline-block', padding: '5px 8px', fontSize: '0.85rem', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            {t('admin.expiryTolerances.others', 'Others')}
                          </span>
                        </td>
                        <td style={{ padding: '4px 8px' }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={tolTexts['default'] ?? String(expiryTolerances.defaultTolerance)}
                            onChange={(e) => {
                              if (/^[\d.,]*$/.test(e.target.value)) {
                                setTolTexts(prev => ({ ...prev, default: e.target.value }));
                              }
                            }}
                            onBlur={(e) => {
                              const raw = e.target.value.replace(',', '.');
                              const parsed = parseFloat(raw);
                              setExpiryTolerances(prev => ({ ...prev, defaultTolerance: isNaN(parsed) ? 0 : parsed }));
                              setTolTexts(prev => ({ ...prev, default: isNaN(parsed) ? '0' : String(parsed) }));
                            }}
                            style={{
                              width: '100px', padding: '5px 8px', fontSize: '0.85rem',
                              border: '1px solid var(--border-color)', borderRadius: '4px',
                              background: 'var(--bg-primary)', color: 'var(--text-primary)',
                            }}
                          />
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>

                  <div style={{ marginTop: '0.75rem' }}>
                    <button
                      className="add-rule-button"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                      onClick={() => {
                        setExpiryTolerances(prev => ({
                          ...prev,
                          currencyTolerances: [...prev.currencyTolerances, { currency: '', tolerance: 0 }],
                        }));
                      }}
                    >
                      + {t('admin.expiryTolerances.addCurrency', 'Add Currency')}
                    </button>
                  </div>
                </div>
              </div>

              {/* Counterparty Expiry Emails — second column */}
              <div className="automation-column">
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>{t('admin.expiryEmails.counterpartyEmails', 'Counterparty Emails')}</h3>
                  </div>
                  <p>{t('admin.expiryEmails.description', 'Configure which email addresses expiry emails are sent to for each counterparty.')}</p>

                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {expiryEmailRows.map((row, idx) => {
                      const usedIds = expiryEmailRows
                        .filter((_, i) => i !== idx)
                        .map(r => r.counterpartyId);
                      const filteredBanks = availableBanks
                        .filter(b => b.isCounterparty && !usedIds.includes(b.id));

                      return (
                        <div key={idx} style={{
                          border: '1px solid var(--border-color)', borderRadius: '6px',
                          padding: '0.75rem', background: 'var(--bg-primary)',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <select
                              value={row.counterpartyId}
                              onChange={(e) => {
                                const updated = [...expiryEmailRows];
                                updated[idx] = { ...updated[idx], counterpartyId: e.target.value };
                                setExpiryEmailRows(updated);
                              }}
                              style={{
                                flex: 1, padding: '5px 8px', fontSize: '0.85rem',
                                border: '1px solid var(--border-color)', borderRadius: '4px',
                                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                              }}
                            >
                              <option value="">{t('admin.expiryEmails.selectCounterparty', '-- Select --')}</option>
                              {row.counterpartyId && !filteredBanks.some(b => b.id === row.counterpartyId) && (() => {
                                const currentBank = availableBanks.find(b => b.id === row.counterpartyId);
                                return currentBank ? <option key={currentBank.id} value={currentBank.id}>{currentBank.name}</option> : null;
                              })()}
                              {filteredBanks.map(bank => (
                                <option key={bank.id} value={bank.id}>{bank.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={() => setExpiryEmailRows(prev => prev.filter((_, i) => i !== idx))}
                              title={t('admin.expiryEmails.remove', 'Remove')}
                              style={{ background: 'none', border: 'none', color: 'var(--accent-red)', cursor: 'pointer', fontSize: '1rem', padding: '0.25rem', marginLeft: '0.5rem', lineHeight: 1 }}
                            >
                              ×
                            </button>
                          </div>
                          <div className="add-contact" style={{ marginBottom: row.emails.length > 0 ? '0.5rem' : 0 }}>
                            <input
                              type="email"
                              placeholder={t('admin.expiryEmails.addEmailPlaceholder', 'Add email...')}
                              value={newExpiryEmailInputs[idx] || ''}
                              onChange={(e) => setNewExpiryEmailInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const val = (newExpiryEmailInputs[idx] || '').trim();
                                  if (val && val.includes('@') && !row.emails.includes(val)) {
                                    const updated = [...expiryEmailRows];
                                    updated[idx] = { ...updated[idx], emails: [...updated[idx].emails, val] };
                                    setExpiryEmailRows(updated);
                                    setNewExpiryEmailInputs(prev => ({ ...prev, [idx]: '' }));
                                  }
                                }
                              }}
                            />
                            <button onClick={() => {
                              const val = (newExpiryEmailInputs[idx] || '').trim();
                              if (val && val.includes('@') && !row.emails.includes(val)) {
                                const updated = [...expiryEmailRows];
                                updated[idx] = { ...updated[idx], emails: [...updated[idx].emails, val] };
                                setExpiryEmailRows(updated);
                                setNewExpiryEmailInputs(prev => ({ ...prev, [idx]: '' }));
                              }
                            }}>{t('admin.expiryEmails.addButton', 'Add')}</button>
                          </div>
                          {row.emails.length > 0 && (
                            <div className="contact-items">
                              {row.emails.map((email, emailIdx) => (
                                <div key={emailIdx} className="contact-item">
                                  <span>{email}</span>
                                  <button onClick={() => {
                                    const updated = [...expiryEmailRows];
                                    updated[idx] = { ...updated[idx], emails: updated[idx].emails.filter((_, i) => i !== emailIdx) };
                                    setExpiryEmailRows(updated);
                                  }}>×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ marginTop: '0.75rem' }}>
                    <button
                      className="add-rule-button"
                      style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                      onClick={() => {
                        setExpiryEmailRows(prev => [...prev, { counterpartyId: '', emails: [] }]);
                      }}
                    >
                      + {t('admin.expiryEmails.addCounterparty', 'Add Counterparty')}
                    </button>
                  </div>

                  {/* Internal CC Recipients */}
                  <div style={{ marginTop: '1.5rem' }}>
                    <h3 style={{ fontSize: '0.95rem', marginBottom: '0.5rem' }}>{t('admin.expiryEmails.ccTitle', 'Internal CC Recipients')}</h3>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                      {t('admin.expiryEmails.ccDescription', 'People within your organisation who should be copied on all expiry emails.')}
                    </p>
                    <div className="contact-list">
                      <div className="add-contact">
                        <input
                          type="email"
                          placeholder={t('admin.expiryEmails.addEmailPlaceholder', 'Add email...')}
                          value={newCcEmailInput}
                          onChange={(e) => setNewCcEmailInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = newCcEmailInput.trim();
                              if (val && val.includes('@') && !expiryEmailCc.includes(val)) {
                                setExpiryEmailCc(prev => [...prev, val]);
                                setNewCcEmailInput('');
                              }
                            }
                          }}
                        />
                        <button onClick={() => {
                          const val = newCcEmailInput.trim();
                          if (val && val.includes('@') && !expiryEmailCc.includes(val)) {
                            setExpiryEmailCc(prev => [...prev, val]);
                            setNewCcEmailInput('');
                          }
                        }}>{t('admin.expiryEmails.addButton', 'Add')}</button>
                      </div>
                      {expiryEmailCc.length > 0 && (
                        <div className="contact-items">
                          {expiryEmailCc.map((email, idx) => (
                            <div key={idx} className="contact-item">
                              <span>{email}</span>
                              <button onClick={() => setExpiryEmailCc(prev => prev.filter((_, i) => i !== idx))}>×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="confirmations-save-actions">
              <button
                disabled={isSaving}
                className="btn-primary"
                onClick={saveConfiguration}
              >
                {isSaving ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
              </button>
            </div>
          </div>
          )
        )}

        {activeTab === 'users' && (
          <UsersPanel />
        )}

        {activeTab === 'valuation' && (
          valuationPreferencesLoading || !valuationPreferencesLoaded ? (
            <div className="tab-loading">
              <div className="spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : (
            <ValuationPreferencesSettings
              settings={valuationPreferences}
              saving={isSavingValuationPreferences}
              onSave={async (prefs) => {
                if (!user?.profile?.organization?.id) return;
                setIsSavingValuationPreferences(true);
                try {
                  const response = await clientService.updateValuationPreferences(
                    user.profile.organization.id,
                    prefs
                  );
                  if (response.success && response.data) {
                    setValuationPreferences(response.data);
                    setAlertModal({
                      isOpen: true,
                      title: t('admin.valuation.saveSuccess', 'Valuation Preferences Saved'),
                      message: t('admin.valuation.saveSuccessMessage', 'Valuation preferences have been saved successfully.'),
                      type: 'success',
                    });
                  } else {
                    setAlertModal({
                      isOpen: true,
                      title: t('admin.valuation.saveError', 'Error'),
                      message: t('admin.valuation.saveErrorMessage', 'Failed to save valuation preferences.'),
                      type: 'error',
                    });
                  }
                } catch (error) {
                  logger.error('Failed to save valuation preferences:', error);
                  setAlertModal({
                    isOpen: true,
                    title: t('admin.valuation.saveError', 'Error'),
                    message: t('admin.valuation.saveErrorMessage', 'Failed to save valuation preferences.'),
                    type: 'error',
                  });
                } finally {
                  setIsSavingValuationPreferences(false);
                }
              }}
            />
          )
        )}

        {activeTab === 'entityaliases' && (
          <EntityAliasesPanel />
        )}

      </div>

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeModal}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onConfirm={alertModal.onConfirm}
        confirmLabel={alertModal.confirmLabel}
      />

    </div>
  );
};

export default AdminDashboard;